/**
 * State-aware Oak tap dialogue — MVP PR A1.
 *
 * Replaces the static `PalletTown_ProfessorOaksLab_EventScript_ProfOak`
 * entry in dialogue.ts (which returned "Hello there! / I am PROF. OAK.
 * / People call me the POKEMON PROF." on every tap regardless of state).
 * The dispatcher reads the lab scene state machine and returns the
 * cartridge-appropriate line per phase.
 *
 * State → dialogue:
 *
 *     ┌───────────────────────────────────────────────────────────┐
 *     │ state │ dialogue (cartridge text key)                     │
 *     ├───────┼───────────────────────────────────────────────────┤
 *     │   0   │ defensive fallback (shouldn't happen — Oak is      │
 *     │       │   hidden pre-intercept; tap surface doesn't exist) │
 *     │   1   │ defensive fallback (Oak is mid-warp during         │
 *     │       │   monologue; tap window should be input-gated)     │
 *     │   2   │ OakWhichOneWillYouChoose — "Now, RED. Inside       │
 *     │       │   those three POKé BALLS are POKéMON. Which one    │
 *     │       │   will you choose for yourself?"                   │
 *     │   3   │ OakCanReachNextTownWithMon — travel advice         │
 *     │   4   │ OakCanReachNextTownWithMon (same as 3 — Oak's      │
 *     │       │   long-term post-cinematic line)                   │
 *     └───────────────────────────────────────────────────────────┘
 *
 * Defensive states (0 + 1) return a minimal "OAK: …" fallback so a
 * desynced tap can't crash dispatch. Production flow should never hit
 * those branches: state 0 = Oak is hidden via FLAG_HIDE_OAK_IN_HIS_LAB,
 * state 1 = runHandlerBracket's transitioning=true gates input.
 */
import type { Handler, ScriptCtx } from '../scriptRunner';
import { getLabSceneState, LAB_SCENE_STATES } from '../labScene';

const OAK_WHICH_ONE = [
  'OAK: Now, RED.',
  'Inside those three POKé BALLS are POKéMON.',
  'Which one will you choose for yourself?',
];

const OAK_CAN_REACH_NEXT_TOWN = [
  'OAK: If a wild POKéMON appears,',
  'your POKéMON can battle it.',
  'With it at your side, you should be able',
  'to reach the next town.',
];

const OAK_DEFENSIVE_FALLBACK = ['OAK: …'];

export const labOakTapHandler: Handler = async (ctx: ScriptCtx) => {
  const state = getLabSceneState();
  let pages: string[];
  switch (state) {
    case LAB_SCENE_STATES.PRE_PICK: {
      // Lab-flow redesign: pre-pick, tapping Oak IS the draft. The
      // modal normally fires at the end of his speech; this is the
      // player-facing retry for any interrupted/dismissed modal (tab
      // close, rescue path, offer-roll race). Falls back to the
      // "which one will you choose" line only when the offers aren't
      // cached (launchStarterDraft would bail silently otherwise).
      const pending = ctx.runState.getPendingDraft();
      if (pending?.zoneId === 'PalletTown_ProfessorOaksLab') {
        await ctx.worldScene.launchStarterDraft();
        return;
      }
      pages = OAK_WHICH_ONE;
      break;
    }
    case LAB_SCENE_STATES.POST_PICK:
    case LAB_SCENE_STATES.SEALED:
      pages = OAK_CAN_REACH_NEXT_TOWN;
      break;
    default:
      pages = OAK_DEFENSIVE_FALLBACK;
      break;
  }
  ctx.worldScene.showDialogue(pages, null);
};
